import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import SearchIcon from "../../assets/icons/SearchIcon";
import RefreshIcon from "../../assets/icons/RefreshIcon";
import { authFetch } from "../../utils/authFetch";

interface Organization {
  id: string;
  name: string;
  requiresApproval: boolean;
  signedUrl?: string;
  membershipStatusId?: 0 | 1 | 14 | 15 | 16 | "none";
  joinRequest?: {
    id?: number;
    requestedAt?: string;
    reviewedAt?: string;
    reviewNotes?: string | null;
  };
}

type JoinRequestStatus = 1 | 14 | 15 | 16;

type ButtonVariant = "default" | "pending" | "accepted" | "rejected" | "joined";

interface JoinOrganizationCardProps {
  onBack: () => void;
  onJoin: (organization: Organization) => void;
  organizations?: Organization[];
  onRefreshStatus?: () => Promise<void> | void;
  inModal?: boolean;
}

const JoinOrganizationCard: React.FC<JoinOrganizationCardProps> = ({
  onBack,
  onJoin,
  organizations,
  onRefreshStatus,
  inModal = false,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [fetchedOrganizations, setFetchedOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(!organizations);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [joiningOrgId, setJoiningOrgId] = useState<string | null>(null);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [refreshTick, setRefreshTick] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim());
    }, 400);

    return () => {
      window.clearTimeout(timer);
    };
  }, [searchQuery]);

  const mapOrganization = (item: any): Organization | null => {
    const idValue = item?.id ?? item?.organizationId ?? item?.orgId;
    const nameValue = item?.name ?? item?.organizationName ?? item?.title;

    if (idValue == null || !nameValue) {
      return null;
    }

    const actions = item?.actions;
    const statusIdValue =
      typeof item?.membershipStatusId === "number"
        ? item.membershipStatusId
        : item?.membershipStatus === "pending"
          ? 1
          : item?.membershipStatus === "accepted"
            ? 14
            : item?.membershipStatus === "rejected"
              ? 15
              : item?.membershipStatus === "joined" || item?.membershipStatus === "member"
                ? 16
                : item?.membershipStatus === "none"
                  ? 0
                  : undefined;
    const requiresApproval = Boolean(
      item?.requiresApproval ??
      item?.approvalRequired ??
      item?.requires_approval ??
      actions?.requiresApproval ??
      actions?.approvalRequired
    );

    return {
      id: String(idValue),
      name: String(nameValue),
      requiresApproval,
      signedUrl: typeof item?.signedUrl === "string" ? item.signedUrl : "",
      membershipStatusId:
        statusIdValue === 0 || statusIdValue === 1 || statusIdValue === 14 || statusIdValue === 15 || statusIdValue === 16
          ? statusIdValue
          : item?.membershipStatusId === "none"
            ? "none"
            : undefined,
      joinRequest: item?.joinRequest
        ? {
          id: item.joinRequest.id,
          requestedAt: item.joinRequest.requestedAt,
          reviewedAt: item.joinRequest.reviewedAt,
          reviewNotes: item.joinRequest.reviewNotes ?? null,
        }
        : undefined,
    };
  };

  const getJoinRequestStatus = (organization: Organization): JoinRequestStatus | null => {
    const candidate =
      typeof organization.membershipStatusId === "number"
        ? organization.membershipStatusId
        : organization.joinRequest
          ? 1
          : null;

    if (candidate === 0) {
      return null;
    }

    if (candidate === 1 || candidate === 14 || candidate === 15 || candidate === 16) {
      return candidate;
    }

    return null;
  };

  const getButtonVariant = (
    status: JoinRequestStatus | null,
    isRequesting: boolean
  ): ButtonVariant => {
    if (isRequesting) {
      return "default";
    }

    if (status === 1) {
      return "pending";
    }

    if (status === 14) {
      return "accepted";
    }

    if (status === 15) {
      return "rejected";
    }

    if (status === 16) {
      return "joined";
    }

    return "default";
  };

  const getJoinButtonLabel = (
    status: JoinRequestStatus | null,
    isRequesting: boolean
  ): string => {
    if (isRequesting) {
      return "Requesting...";
    }

    if (status === 1) {
      return "Request Pending";
    }

    if (status === 14) {
      return "Request Accepted";
    }

    if (status === 15) {
      return "Request Rejected";
    }

    if (status === 16) {
      return "Joined";
    }

    return "Request to Join";
  };

  useEffect(() => {
    if (organizations) {
      return;
    }

    let isMounted = true;

    const loadOrganizations = async () => {
      const apiBase = process.env.REACT_APP_API_URL;
      if (!apiBase) {
        if (isMounted) {
          setLoadError("API URL is not configured.");
        }
        return;
      }

      setIsLoading(true);
      setLoadError(null);

      try {
        const params = new URLSearchParams({ page: "1", limit: "20" });
        if (debouncedSearchQuery) {
          params.set("query", debouncedSearchQuery);
        }

        const response = await authFetch(`${apiBase}/portal/organizations?${params.toString()}`);
        const json: any = await response.json();

        if (!response.ok) {
          throw new Error(json?.message || "Failed to load organizations.");
        }

        const source: any[] =
          json?.organizations ??
          json?.data?.organizations ??
          json?.data ??
          json?.results ??
          [];

        const normalized = Array.isArray(source)
          ? source
            .map(mapOrganization)
            .filter((org): org is Organization => !!org)
          : [];

        if (isMounted) {
          setFetchedOrganizations(normalized);
          setPage(1);
          setTotalPages(Number(json?.meta?.totalPages) || 1);
        }
      } catch (error) {
        if (isMounted) {
          setLoadError(
            error instanceof Error ? error.message : "Failed to load organizations."
          );
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void loadOrganizations();

    return () => {
      isMounted = false;
    };
  }, [organizations, debouncedSearchQuery, refreshTick]);

  const handleRefresh = async () => {
    setIsRefreshing(true);

    try {
      await onRefreshStatus?.();
      setRefreshTick((current) => current + 1);
    } finally {
      setIsRefreshing(false);
    }
  };

  const orgsToDisplay = organizations || fetchedOrganizations;

  const hasMorePages = !organizations && page < totalPages;

  const loadMoreOrganizations = async () => {
    if (organizations || isLoadingMore || isLoading || !hasMorePages) {
      return;
    }

    const apiBase = process.env.REACT_APP_API_URL;
    if (!apiBase) {
      setLoadError("API URL is not configured.");
      return;
    }

    const nextPage = page + 1;
    setIsLoadingMore(true);
    setLoadError(null);

    try {
      const params = new URLSearchParams({ page: String(nextPage), limit: "20" });
      if (debouncedSearchQuery) {
        params.set("query", debouncedSearchQuery);
      }

      const response = await authFetch(
        `${apiBase}/portal/organizations?${params.toString()}`
      );
      const json: any = await response.json();

      if (!response.ok) {
        throw new Error(json?.message || "Failed to load organizations.");
      }

      const source: any[] =
        json?.organizations ??
        json?.data?.organizations ??
        json?.data ??
        json?.results ??
        [];

      const normalized = Array.isArray(source)
        ? source
          .map(mapOrganization)
          .filter((org): org is Organization => !!org)
        : [];

      setFetchedOrganizations((prev) => {
        const existingIds = new Set(prev.map((org) => org.id));
        const uniqueNew = normalized.filter((org) => !existingIds.has(org.id));
        return [...prev, ...uniqueNew];
      });
      setPage(nextPage);
      setTotalPages(Number(json?.meta?.totalPages) || totalPages);
    } catch (error) {
      setLoadError(
        error instanceof Error ? error.message : "Failed to load organizations."
      );
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleListScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const nearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 24;

    if (nearBottom) {
      void loadMoreOrganizations();
    }
  };

  const openOrganizationStatus = (organization: Organization) => {
    const status = getJoinRequestStatus(organization);

    if (status === 16) {
      return;
    }

    if (status === 1 || status === 14 || status === 15) {
      onJoin(organization);
      return;
    }

    void handleJoinRequest(organization);
  };

  const handleJoinRequest = async (organization: Organization) => {
    const apiBase = process.env.REACT_APP_API_URL;
    if (!apiBase) {
      setJoinError("API URL is not configured.");
      return;
    }

    setJoinError(null);
    setJoiningOrgId(organization.id);

    try {
      const response = await authFetch(
        `${apiBase}/portal/organizations/${encodeURIComponent(organization.id)}/join-request`,
        { method: "POST" }
      );

      let responseBody: any = null;
      try {
        responseBody = await response.json();
      } catch {
        responseBody = null;
      }

      if (!response.ok) {
        throw new Error(responseBody?.message || "Failed to send join request.");
      }

      const updatedOrganization: Organization = {
        ...organization,
        membershipStatusId: 1,
        joinRequest: {
          id: responseBody?.data?.joinRequest?.id,
          requestedAt:
            responseBody?.data?.joinRequest?.requestedAt || new Date().toISOString(),
          reviewedAt: responseBody?.data?.joinRequest?.reviewedAt,
          reviewNotes: responseBody?.data?.joinRequest?.reviewNotes ?? null,
        },
      };

      onJoin(updatedOrganization);
    } catch (error) {
      setJoinError(
        error instanceof Error ? error.message : "Failed to send join request."
      );
    } finally {
      setJoiningOrgId(null);
    }
  };

  const filteredOrganizations = useMemo(() => {
    if (!organizations) {
      return orgsToDisplay;
    }

    if (!searchQuery.trim()) return orgsToDisplay;

    return orgsToDisplay.filter((org: Organization) =>
      org.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery, orgsToDisplay]);

  const getOrgInitials = (name: string) => {
    return name
      .split(" ")
      .slice(0, 2)
      .map((word) => word[0])
      .join("")
      .toUpperCase();
  };

  return (
    <CardContainer $inModal={inModal}>
      <CardTitle>Select Organization You Want To Link With</CardTitle>
      <CardSubtitle>
        Search or select organization you want to link with, simply enter your organization
        name and it will be appeared in dropdown with a button to request to join.
      </CardSubtitle>

      <OrganizationsSection>
        <SearchBoxWrapper>
          <SearchIconWrapper>
            <SearchIcon size={20} />
          </SearchIconWrapper>
          <SearchInput
            type="text"
            placeholder="Search organization here"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchBoxWrapper>
        {!organizations && isLoading && (
          <NoResults>Loading organizations...</NoResults>
        )}
        {!organizations && !isLoading && loadError && (
          <NoResults>{loadError}</NoResults>
        )}
        {joinError && <NoResults>{joinError}</NoResults>}
        {filteredOrganizations.length > 0 && (
          <>
            <SectionHeader>
              <SectionLabel>Suggested Organizations</SectionLabel>
              <RefreshButton
                type="button"
                onClick={() => {
                  void handleRefresh();
                }}
                disabled={isLoading || isLoadingMore || isRefreshing}
                aria-label="Refetch organizations"
                title="Refetch organizations"
              >
                <RefreshIcon />
              </RefreshButton>
            </SectionHeader>
            <OrganizationsList onScroll={handleListScroll}>
              {filteredOrganizations.map((org) => {
                const status = getJoinRequestStatus(org);
                const isRequesting = joiningOrgId === org.id;
                const buttonVariant = getButtonVariant(status, isRequesting);
                const isDisabled = Boolean(joiningOrgId) || status === 1 || status === 14 || status === 16;

                return (
                  <OrganizationItem
                    key={org.id}
                    onClick={() => {
                      openOrganizationStatus(org);
                    }}
                  >
                    <OrgLogo>
                      {org.signedUrl ? (
                        <OrgLogoImage src={org.signedUrl} alt={`${org.name} logo`} />
                      ) : (
                        getOrgInitials(org.name)
                      )}
                    </OrgLogo>
                    <OrgInfo>
                      <OrgName>{org.name}</OrgName>
                      <OrgId>Id:{org.id}</OrgId>
                    </OrgInfo>
                    <JoinButton
                      $variant={buttonVariant}
                      onClick={(event) => {
                        event.stopPropagation();
                        openOrganizationStatus(org);
                      }}
                      disabled={isDisabled}
                    >
                      {getJoinButtonLabel(status, isRequesting)}
                    </JoinButton>
                  </OrganizationItem>
                );
              })}
            </OrganizationsList>
            {!organizations && isLoadingMore && (
              <NoResults>Loading more organizations...</NoResults>
            )}
          </>
        )}
        {!isLoading && !loadError && filteredOrganizations.length === 0 && (
          <NoResults>No organizations found matching your search.</NoResults>
        )}
      </OrganizationsSection>
      <BackButton onClick={onBack}>Go Back</BackButton>
    </CardContainer>
  );
};

const CardContainer = styled.div<{ $inModal?: boolean }>`
  position: relative;
  z-index: 3;
  width: ${({ $inModal }) => ($inModal ? "100%" : "min(760px, calc(100% - 48px))")};
  margin: ${({ $inModal }) => ($inModal ? "0" : "16px auto")};
  padding: ${({ $inModal }) => ($inModal ? "0" : "0")};
`;

const BackButton = styled.button`
  display: block;
  margin: 24px auto 0;
  padding: 10px 12px;
  min-width: 88px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #f5f5f7;
  color: #4b5563;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);

  &:hover {
    background: #e5e7eb;
    box-shadow: 0 2px 6px rgba(15, 23, 42, 0.12);
  }

  &:active {
    transform: translateY(1px);
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.16);
  }
`;

const CardTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  line-height: 1.12;
  color: #003366;
  font-weight: 600;
  text-align: center;
`;

const CardSubtitle = styled.p`
  margin: 28px auto 44px;
  max-width: 1040px;
  color: #585558;
  font-size: 14px;
  line-height: 1.55;
  font-weight: 400;
  text-align: center;
`;

const SearchBoxWrapper = styled.div`
  display: flex;
  align-items: center;
  background: #ececee;
  border: 1px solid #dedee3;
  border-radius: 10px;
  padding: 8px;
  margin: 0 auto 16px;
  width: 100%;
  gap: 14px;
`;

const SearchIconWrapper = styled.div`
  display: flex;
  align-items: center;
  color: #9ca3af;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  font-size: 16px;
  line-height: 1.2;
  color: #585558;
  font-weight: 400;
  outline: none;

  &::placeholder {
    color: #757b86;
  }
`;

const OrganizationsSection = styled.div`
  background: rgba(255, 255, 255, 0.97);
  text-align: left;
  border-radius: 12px;
  padding:8px;
  border: 1px solid #dedee3;
  border-bottom:none;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
`;

const SectionLabel = styled.h3`
  margin: 0 0 10px;
  font-size: 12px;
  font-weight: 600;
  color: #ff6f3f;
  text-transform: none;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const RefreshButton = styled.button`
  border: none;
  background: transparent;
  padding: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const OrganizationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  overflow-y: auto;
  max-height: 270px;
`;

const OrganizationItem = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding:8px;
  border-bottom: 1px solid #d7d9df;
  background: transparent;
  transition: all 0.2s ease;
  cursor: pointer;

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.45);
  }
`;

const OrgLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #003366 0%, #1d5aa3 100%);
  color: white;
  font-size: 16px;
  font-weight: 600;
  flex-shrink: 0;
  overflow: hidden;
`;

const OrgLogoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const OrgInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const OrgName = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #212933;
`;

const OrgId = styled.p`
  margin: 0;
  font-size: 12px;
  color: #585558;
  font-weight: 400;
`;

const ApprovalBadge = styled.span`
  display: inline-block;
  margin-top: 4px;
  width: fit-content;
  padding: 4px 12px;
  background: rgba(255, 153, 0, 0.1);
  border: 1px solid rgba(255, 153, 0, 0.3);
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  color: #d97706;
`;

const JoinStatusTag = styled.span<{ $status: JoinRequestStatus }>`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  margin-top: 4px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 600;
  background: ${({ $status }) => {
    if ($status === 14) {
      return "#e8f8ee";
    }
    if ($status === 15) {
      return "#fdecec";
    }
    return "#fff5e8";
  }};
  color: ${({ $status }) => {
    if ($status === 14) {
      return "#15803d";
    }
    if ($status === 15) {
      return "#b42318";
    }
    return "#b54708";
  }};
  border: 1px solid ${({ $status }) => {
    if ($status === 14) {
      return "#b7ebc6";
    }
    if ($status === 15) {
      return "#f6c7c7";
    }
    return "#f7d9b0";
  }};
`;

const JoinButton = styled.button<{ $variant: ButtonVariant }>`
  min-width: 115px;
  padding: 10px;
  background: ${({ $variant }) => {
    if ($variant === "accepted") {
      return "#17803d";
    }
    if ($variant === "pending") {
      return "#d97706";
    }
    if ($variant === "rejected") {
      return "#c23434";
    }
    if ($variant === "joined") {
      return "#6b7280";
    }
    return "#0556cb";
  }};
  color: white;
  border: none;
  border-radius: 5px;
  font-size: 14px;
  line-height: 1.2;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  white-space: nowrap;

  &:hover {
    background: ${({ $variant }) => {
    if ($variant === "accepted") {
      return "#146b34";
    }
    if ($variant === "pending") {
      return "#b86605";
    }
    if ($variant === "rejected") {
      return "#a62b2b";
    }
    if ($variant === "joined") {
      return "#4b5563";
    }
    return "#044aa8";
  }};
    box-shadow: 0 4px 12px
      ${({ $variant }) => {
    if ($variant === "accepted") {
      return "rgba(23, 128, 61, 0.24)";
    }
    if ($variant === "pending") {
      return "rgba(217, 119, 6, 0.24)";
    }
    if ($variant === "rejected") {
      return "rgba(194, 52, 52, 0.24)";
    }
    if ($variant === "joined") {
      return "rgba(107, 114, 128, 0.24)";
    }
    return "rgba(5, 86, 203, 0.2)";
  }};
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const NoResults = styled.div`
  padding: 24px 12px;
  text-align: center;
  color: #585558;
  font-size: 14px;
  font-weight: 400;
`;

export default JoinOrganizationCard;
